import https from 'https';
import { unsafeCoerce } from 'fp-ts/lib/function';
import fetch from 'node-fetch';
import { addQueryParams } from '../../routing/addQueryParams';
import { AsyncCache, AsyncCacheEntry } from '../../util/AsyncCache/AsyncCache';
import { Batcher } from '../../util/Batcher/Batcher';
import { env } from '../../util/env';
import { parseFromFetchResponse } from '../../util/parse';
import { LoggerService } from '../LoggerService/LoggerService';
import { NetworkService } from '../NetworkService';
import { SsrData } from '../SsrData';
import {
  BatchEvalResultValidator,
  EvalContext,
  EvalResult,
  EvalResultValidator,
  FlagrData,
  FlagrEntity,
  FlagrFlag,
} from './types';

const JSON_HEADERS = {
  accept: 'application/json, text/plain, */*',
  'content-type': 'application/json;charset=UTF-8',
};

const UNICRON_TAGS = ['Unicron', 'unicron'];

export class FlagrService {
  constructor(private network: NetworkService, private ssrData: SsrData) {}

  private cache = new AsyncCache<FlagrData>(this.ssrData.slot('FLAGR_SERVICE_CACHE', () => ({})));

  /** Tracks which keys have already been logged to analytics */
  public analyticsLoggedKeys = new Set<string>();

  private evaluateBatched = (queries: EvalContext[]): Promise<FlagrData[]> =>
    Promise.all(
      queries.map(async query => {
        const fetched = await this.network.fetch(env.FLAGR_ENDPOINT, {
          body: JSON.stringify(query),
          headers: JSON_HEADERS,
          method: 'POST',
        });

        const { variantKey, variantAttachment } = await parseFromFetchResponse(
          fetched,
          EvalResultValidator,
        );

        if (variantKey === undefined) {
          return null;
        } else {
          return { key: variantKey, attachment: variantAttachment };
        }
      }),
    );

  private evaluationBatcher = new Batcher(this.evaluateBatched);

  public evaluate = (flagKey: string, entity?: FlagrEntity): AsyncCacheEntry<FlagrData> => {
    const cacheKey = `${flagKey}:${entity?.type}:${entity?.id}`;

    return this.cache.run(cacheKey, () =>
      this.evaluationBatcher.call({
        entityContext: entity?.context,
        entityID: entity?.id,
        entityType: entity?.type,
        flagKey,
      }),
    );
  };

  public evaluateBatch = async (
    flagKeys: string[],
    entityIds: string[],
    enableDebug?: boolean,
  ): Promise<EvalResult[]> => {
    const entities = entityIds.map(id => ({ entityID: id }));

    const fetched = await this.network.fetch(`${env.FLAGR_ENDPOINT}/batch`, {
      body: JSON.stringify({ flagKeys, enableDebug, entities }),
      headers: JSON_HEADERS,
      method: 'POST',
    });

    const { evaluationResults } = await parseFromFetchResponse(fetched, BatchEvalResultValidator);
    return evaluationResults;
  };

  public prefetch = (flagKey: string, entity?: FlagrEntity) =>
    this.evaluate(flagKey, entity).promise;

  public booleanEvaluate = (flagKey: string, entity?: FlagrEntity) =>
    this.prefetch(flagKey, entity).then(it => Boolean(it.data?.key));

  public flags = async (logger?: LoggerService): Promise<FlagrFlag[]> => {
    /*
     * Since this is server side, don't hit the external ingress. Also don't want to go through
     * the SSO proxy, hence using the cluster local address.
     */
    try {
      const flagrInternalUrlBase =
        process.env.NODE_ENV === 'development'
          ? 'https://flagr.dev.he0.io/api/v1/flags'
          : `https://flagr.flagr.svc.cluster.local/api/v1/flags`;

      const flagrUrl = addQueryParams(flagrInternalUrlBase, {
        preload: 'true',
        tags: UNICRON_TAGS.join(','),
      });

      const agent = new https.Agent({ rejectUnauthorized: false });

      const fetched = await fetch(flagrUrl, {
        headers: JSON_HEADERS,
        method: 'GET',
        agent,
      });

      return unsafeCoerce(await fetched.json());
    } catch (e) {
      logger?.error('Flagr flags preload error', { flagrError: JSON.stringify(e) });
      return [];
    }
  };
}
